import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { landingPath } from '../utils/navigation';
import { hasAnyPermission, hasPermission } from '../utils/roleUtils';
import EmptyState from './EmptyState';
import { FlowLoader } from './LoadingSpinner';

export const AccessDenied = () => (
  <div className="card">
    <EmptyState
      title="Nothing to open"
      body="Your role does not include a page you can view."
    />
  </div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
  requires?: string;
  /** Any one of these permissions is enough. */
  requiresAny?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requires, requiresAny }) => {
  const { user, loading, permissions, sessionLoading } = useAuth();
  const location = useLocation();

  // Before the shell exists there is nothing to anchor a small loader to, so the
  // session check fills the screen.
  if (loading) {
    return <FlowLoader fullScreen label="Signing you in" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const restricted = Boolean(requires) || Boolean(requiresAny?.length);
  const allowed = requiresAny?.length
    ? hasAnyPermission(permissions, requiresAny)
    : !requires || hasPermission(permissions, requires);

  if (restricted) {
    // Waiting avoids bouncing a permitted user off the page before their role resolves.
    if (sessionLoading) {
      return <FlowLoader fullScreen label="Checking your access" />;
    }
    if (!allowed) {
      const next = landingPath(permissions);
      if (next && next !== location.pathname) {
        return <Navigate to={next} replace />;
      }
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
};
